import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ButtonControl, IconControl } from "spark-core-dx/components";
import { useCoreContext, useCoreStateContext } from "spark-core-dx/contexts";
import { useGenericManager } from "spark-core-dx/hooks";
import { ItemServiceFactory } from "../../../services/ItemServiceFactory";
import { Operations } from "../../../services/ItemServices";
import ResourceMaturityBadge from "../../../components/ResourceMaturityBadge";
import { useHelpSupport } from "../../../components/helpSupport";
import { sanitizeHtmlFragment } from "../../../utilities/markupSanitizer";
import {
  fallbackMarketplaceCatalog,
  getAvailabilityLabel,
  getCategory,
  getMarketplaceOfferSearchValues,
  getMarketplaceResourceGroups,
  getPrimaryActionLabel,
  getProduct,
  getProductResources,
  getVisibleResources,
  marketplaceCategories,
  matchesMarketplaceSearch,
  normalizeMarketplaceCatalog,
  resolveMarketplaceProductSlug,
  toMarketplaceSlug
} from "../data/marketplaceCatalog";
import PublicSiteHeader from "../../public/components/PublicSiteHeader";
import MarketplaceAllowancePurchaseDialog from "./MarketplaceAllowancePurchaseDialog";
import MarketplaceConfigurationChooserDialog from "./MarketplaceConfigurationChooserDialog";
import MarketplaceRuntimeCapacityPurchaseDialog, { getRuntimeCapacityActionVerb, hasRuntimeCapacityClientOffer } from "./MarketplaceRuntimeCapacityPurchaseDialog";
import { MARKETPLACE_CATALOG_INVALIDATED_EVENT } from "../marketplaceCheckout";
import { buildResourceConfigurePath } from "../../resource/resourceRoutes";
import HeroBg from "../../../content/homeImages/hero.png";
import HeroBgLow from "../../../content/homeImages/hero-low.jpg";
import "./marketplace.scss";

const featuredResourceKeys = [
  "OdooSource",
  "AzureSQLWarehouse",
  "X12850InboundEDI"
];

const MARKETPLACE_FILTER_ALL = "All";
const MARKETPLACE_FILTER_MY_RESOURCES = "MyResources";
const UNLIMITED_INCLUDED_USAGE_UNITS = 2147483647;
const CLIENT_RESOURCE_ITEM = "ClientResource";

const modeLabel = {
  public: "Convoke Marketplace",
  secure: "Marketplace"
};

const resolveDetailTab = (searchParams) => {
  const tab = searchParams.get("tab")?.toLowerCase();
  if (tab === "plans" || tab === "resources" || tab === "options") return "options";
  return "overview";
};

const normalizeBasePath = (basePath) => {
  const normalized = basePath || "/marketplace";
  const absolutePath = normalized.startsWith("/") ? normalized : `/${normalized}`;
  return absolutePath.length > 1 ? absolutePath.replace(/\/+$/, "") : absolutePath;
};

const withMarketplaceTab = (path, tab) => {
  if (!path) {
    return "";
  }

  const separator = path.includes("?") ? "&" : "?";
  return `${path}${separator}tab=${encodeURIComponent(tab)}`;
};

const normalizeAddedClientResource = (resource, clientResource) => ({
  id: clientResource?._id || clientResource?.id || clientResource?.UniqueId || "",
  _id: clientResource?._id || clientResource?.id || clientResource?.UniqueId || "",
  productKey: clientResource?.ProductKey || clientResource?.productKey || resource?.productKey || "",
  productResourceKey: clientResource?.ProductResourceKey || clientResource?.productResourceKey || resource?.key || "",
  productResourceDefinitionId: clientResource?.ProductResourceDefinitionId || clientResource?.productResourceDefinitionId || resource?.id || "",
  displayName: clientResource?.DisplayName || clientResource?.displayName || resource?.name || "",
  lifecycleStatus: clientResource?.LifecycleStatus ?? clientResource?.lifecycleStatus ?? 4,
  approvalStatus: clientResource?.ApprovalStatus ?? clientResource?.approvalStatus ?? 3,
  configurationCounts: clientResource?.ResourceConfigurationCountSummary || clientResource?.resourceConfigurationCountSummary || {}
});

const getFirst = (items) => {
  if (!items) return null;
  if (typeof items.first === "function") return items.first();
  return Array.isArray(items) ? items[0] : null;
};

const getClientResourceId = (clientResource, result = null) =>
  clientResource?._id
  || clientResource?.id
  || clientResource?.UniqueId
  || result?.Value?.clientResourceId
  || result?.value?.clientResourceId
  || "";

const ACTIVE_MARKETPLACE_CONFIGURATION_STATUSES = new Set([4, 7]);

const hasActiveResourceConfiguration = (resource, catalog, ownedClientResourceLookup) => {
  const clientResourceId = getClientResourceId(ownedClientResourceLookup?.get(resource?.key));
  if (!resource?.key || !clientResourceId) {
    return false;
  }

  return (catalog?.resourceConfigurations || []).some((configuration) =>
    configuration.clientResourceId === clientResourceId
    && configuration.productResourceKey === resource.key
    && ACTIVE_MARKETPLACE_CONFIGURATION_STATUSES.has(Number(configuration.configurationStatus))
  );
};

const hasUnlimitedResourceEntitlement = (ownedClientResource, isMarketplaceUnlimited) =>
  Boolean(isMarketplaceUnlimited || ownedClientResource?.effectiveAllowance?.isUnlimited);

export const buildMarketplaceSalesSupportMessage = ({ product, resource } = {}) => {
  const subject = resource?.name || product?.name || "this Marketplace offering";
  const lines = [`I’m interested in learning more about ${subject}.`];
  if (product?.name && resource?.name) lines.push(`Product: ${product.name}`);
  if (resource?.name) lines.push(`Resource: ${resource.name}`);
  lines.push("Please contact me to discuss pricing, implementation, and next steps.");
  return lines.join("\n");
};

const MarketplaceExperience = ({
  mode = "secure",
  basePath: basePathOverride,
  productSlugOverride,
  resourceSlugOverride,
  tabOverride
}) => {
  const publicMode = mode === "public";
  const coreContext = useCoreContext();
  const coreState = useCoreStateContext();
  const { openHelpSupport } = useHelpSupport();
  const clientResourceMgr = useGenericManager(CLIENT_RESOURCE_ITEM);
  const coreContextRef = useRef(coreContext);
  const coreStateRef = useRef(coreState);
  const clientResourceMgrRef = useRef(clientResourceMgr);
  const routeParams = useParams();
  const productSlug = productSlugOverride ?? routeParams.productSlug;
  const resourceSlug = resourceSlugOverride ?? routeParams.resourceSlug;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [query, setQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState(MARKETPLACE_FILTER_ALL);
  const [activeTab, setActiveTab] = useState(tabOverride || "overview");
  const [heroImageLoaded, setHeroImageLoaded] = useState(false);
  const [catalog, setCatalog] = useState(fallbackMarketplaceCatalog);
  const [purchaseDialogResource, setPurchaseDialogResource] = useState(null);
  const [purchaseDialogOfferKey, setPurchaseDialogOfferKey] = useState("");
  const [capacityPurchaseDialogProduct, setCapacityPurchaseDialogProduct] = useState(null);
  const [capacityPurchaseDialogOfferKey, setCapacityPurchaseDialogOfferKey] = useState("");
  const [configureDialogResource, setConfigureDialogResource] = useState(null);
  const [directAddResourceKey, setDirectAddResourceKey] = useState("");
  const [addedClientResources, setAddedClientResources] = useState(() => new Map());
  const catalogLoadKey = [
    coreContext?.Profile?._id,
    coreContext?.Profile?.ClientId,
    coreContext?.Profile?.AppId
  ].filter(Boolean).join("|");

  const products = catalog.products;
  const resources = catalog.resources;
  const basePath = normalizeBasePath(basePathOverride ?? (publicMode ? "/marketplace" : "/Marketplace"));
  const resolvedProductSlug = resolveMarketplaceProductSlug(productSlug);
  const productPath = (product) => `${basePath}/products/${product.slug || toMarketplaceSlug(product.name)}`;
  const resourcePath = (resource) => `${basePath}/options/${resource.slug || toMarketplaceSlug(resource.name)}`;
  const openMarketplaceSalesSupport = useCallback(({ product, resource }) => {
    openHelpSupport({
      variant: "sales",
      source: "Marketplace Sales",
      initialMessage: buildMarketplaceSalesSupportMessage({ product, resource })
    });
  }, [openHelpSupport]);

  useEffect(() => {
    if (productSlug && resolvedProductSlug !== productSlug) {
      navigate(withMarketplaceTab(`${basePath}/products/${resolvedProductSlug}`, "resources"), { replace: true });
    }
  }, [basePath, navigate, productSlug, resolvedProductSlug]);

  const visibleResources = useMemo(() => getVisibleResources(resources), [resources]);
  const productSlugLookup = useMemo(
    () => new Map(products.map((product) => [product.slug || toMarketplaceSlug(product.name), product])),
    [products]
  );
  const resourceSlugLookup = useMemo(
    () => new Map(visibleResources.map((resource) => [resource.slug || toMarketplaceSlug(resource.name), resource])),
    [visibleResources]
  );
  const resourceKeyLookup = useMemo(
    () => new Map(visibleResources.map((resource) => [resource.key, resource])),
    [visibleResources]
  );
  const ownedClientResourceLookup = useMemo(() => {
    const lookup = new Map();
    (catalog.clientResources || []).forEach((clientResource) => {
      if (clientResource?.productResourceKey) {
        lookup.set(clientResource.productResourceKey, clientResource);
      }
    });
    addedClientResources.forEach((clientResource, productResourceKey) => {
      lookup.set(productResourceKey, clientResource);
    });
    return lookup;
  }, [addedClientResources, catalog.clientResources]);
  const ownedActionMode = catalog.isSelfHostedResourceMode ? "added" : "purchased";
  const isMyResourcesFilter = selectedCategory === MARKETPLACE_FILTER_MY_RESOURCES;

  const selectedResource = resourceSlug ? resourceSlugLookup.get(resourceSlug) : null;
  const selectedProduct = selectedResource
    ? getProduct(selectedResource.productKey, products)
    : resolvedProductSlug
      ? productSlugLookup.get(resolvedProductSlug)
      : null;
  const notFound = Boolean((resourceSlug && !selectedResource) || (productSlug && !selectedProduct));

  const filteredProducts = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    return products.filter((product) => {
      if (selectedCategory === MARKETPLACE_FILTER_MY_RESOURCES) {
        const hasOwnedResource = visibleResources.some((resource) =>
          resource.productKey === product.key && ownedClientResourceLookup.has(resource.key)
        );
        if (!hasOwnedResource) return false;
      }

      const categoryMatch = selectedCategory === MARKETPLACE_FILTER_ALL
        || selectedCategory === MARKETPLACE_FILTER_MY_RESOURCES
        || product.categoryKey === selectedCategory;
      if (!categoryMatch) return false;
      if (!normalizedQuery) return true;

      const resourceMatch = visibleResources.some((resource) =>
        resource.productKey === product.key && matchesMarketplaceSearch(normalizedQuery, [
          resource.name,
          resource.summary,
          ...getMarketplaceOfferSearchValues(resource.offers)
        ])
      );
      return matchesMarketplaceSearch(normalizedQuery, [
        product.name,
        product.summary,
        product.categoryKey,
        ...product.modules
      ]) || resourceMatch;
    });
  }, [ownedClientResourceLookup, products, query, selectedCategory, visibleResources]);

  const filteredResources = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    return visibleResources.filter((resource) => {
      const product = getProduct(resource.productKey, products);
      const categoryMatch = selectedCategory === MARKETPLACE_FILTER_MY_RESOURCES
        ? ownedClientResourceLookup.has(resource.key)
        : selectedCategory === MARKETPLACE_FILTER_ALL || product?.categoryKey === selectedCategory;
      if (!categoryMatch) return false;
      if (!normalizedQuery) return true;

      return matchesMarketplaceSearch(normalizedQuery, [
        resource.name,
        resource.summary,
        product?.name,
        product?.categoryKey,
        ...getMarketplaceOfferSearchValues(resource.offers)
      ]);
    });
  }, [ownedClientResourceLookup, products, query, selectedCategory, visibleResources]);

  const featuredResources = featuredResourceKeys
    .map((key) => resourceKeyLookup.get(key))
    .filter(Boolean);

  useEffect(() => {
    coreContextRef.current = coreContext;
    coreStateRef.current = coreState;
  }, [coreContext, coreState]);

  useEffect(() => {
    clientResourceMgrRef.current = clientResourceMgr;
  }, [clientResourceMgr]);

  const loadCatalog = useCallback(async (isMounted = () => true) => {
    try {
      const apiAvailable = await isMarketplaceApiAvailable();
      if (!apiAvailable) {
        return;
      }

      const activeCoreContext = coreContextRef.current;
      const activeCoreState = coreStateRef.current;
      const factory = activeCoreContext?.itemServiceFactory ?? new ItemServiceFactory();
      const service = factory.GetItemService(
        factory.ItemServiceEnum.ProductMarketplaceCatalog,
        activeCoreState,
        activeCoreContext,
        true
      );
      const response = await service.getOperation({
        operationType: Operations.ProductMarketplaceCatalogItem.LoadProductMarketplaceCatalog,
        isWarning: true,
        supressAllErrors: true
      });
      const result = response?.first?.() ?? response?.[0] ?? response;
      const catalogItem = result?.Items?.first?.() ?? result?.Items?.[0] ?? result?.Value;

      if (isMounted() && result?.Success && catalogItem) {
        setCatalog(normalizeMarketplaceCatalog(catalogItem));
      }
    } catch {
      if (isMounted()) {
        setCatalog(fallbackMarketplaceCatalog);
      }
    }
  }, []);

  useEffect(() => {
    let mounted = true;
    loadCatalog(() => mounted);

    return () => {
      mounted = false;
    };
  }, [catalogLoadKey, loadCatalog, publicMode]);

  useEffect(() => {
    const handleCatalogInvalidated = () => void loadCatalog();
    window.addEventListener(MARKETPLACE_CATALOG_INVALIDATED_EVENT, handleCatalogInvalidated);
    return () => window.removeEventListener(MARKETPLACE_CATALOG_INVALIDATED_EVENT, handleCatalogInvalidated);
  }, [loadCatalog]);

  useEffect(() => {
    setActiveTab(tabOverride || resolveDetailTab(searchParams));
  }, [productSlug, resourceSlug, searchParams, tabOverride]);

  const navigateToConfiguration = (clientResource) => {
    const clientResourceId = getClientResourceId(clientResource);
    if (clientResourceId) {
      navigate(buildResourceConfigurePath(clientResourceId));
    }
  };

  const handleUnlimitedAddResource = async (resource) => {
    const existingClientResource = ownedClientResourceLookup.get(resource?.key);
    if (existingClientResource) {
      navigateToConfiguration(existingClientResource);
      return;
    }

    const activeClientResourceMgr = clientResourceMgrRef.current;
    if (!resource || !activeClientResourceMgr || directAddResourceKey === resource.key) {
      return;
    }

    setDirectAddResourceKey(resource.key);

    const product = getProduct(resource.productKey, products);
    const payload = {
      IsNew: true,
      EnvironmentKey: catalog.environmentKey || "",
      ProductKey: resource.productKey || product?.key || "",
      ProductResourceKey: resource.key,
      ProductResourceDefinitionId: resource.id || resource._id || resource.ProductResourceDefinitionId || "",
      ProductName: product?.name || "",
      ResourceName: resource.name || "",
      DisplayName: resource.name || "",
      Notes: ""
    };

    try {
      const result = await activeClientResourceMgr.RunOperation({
        operationName: Operations.ClientResourceItem.AddMarketplaceClientResource,
        item: payload,
        isItem: true,
        isWarning: true,
        supressAllErrors: false
      });

      if (result?.Success) {
        const addedClientResource = getFirst(result.Items) || result.Item || result.item || null;
        handleResourceAdded(resource, addedClientResource || {
          _id: getClientResourceId(null, result),
          ProductKey: payload.ProductKey,
          ProductResourceKey: payload.ProductResourceKey,
          ProductResourceDefinitionId: payload.ProductResourceDefinitionId,
          DisplayName: payload.DisplayName
        });
        const clientResourceId = getClientResourceId(addedClientResource, result);
        if (clientResourceId) {
          navigate(buildResourceConfigurePath(clientResourceId));
        }
      }
    } finally {
      setDirectAddResourceKey("");
    }
  };

  const handleAddResource = (resource, offerKey = "", options = {}) => {
    if (!publicMode && (resource?.availability === "unlimited" || resource?.availability === "unlocked" || resource?.availability === "available")) {
      const existingClientResource = ownedClientResourceLookup.get(resource?.key);
      if (resource?.isSingleInstance && existingClientResource) {
        navigateToConfiguration(existingClientResource);
        return;
      }
      if (catalog.isMarketplaceUnlimited && !options.forcePurchase) {
        handleUnlimitedAddResource(resource);
        return;
      }
      setPurchaseDialogResource(resource);
      setPurchaseDialogOfferKey(offerKey || resource?.selectedProductOfferKey || "");
    }
  };

  const handleConfigureResource = (resource, options = {}) => {
    if (!publicMode && resource?.key) {
      if (options.startNew) {
        const clientResource = ownedClientResourceLookup.get(resource.key);
        const clientResourceId = getClientResourceId(clientResource);
        if (clientResourceId) {
          navigate(`${buildResourceConfigurePath(clientResourceId)}?new=1`);
          return;
        }
      }
      setConfigureDialogResource(resource);
    }
  };

  const handleAddRuntimeCapacityOffer = (product, offerKey = "") => {
    if (publicMode || !product) {
      return;
    }

    setCapacityPurchaseDialogProduct(product);
    setCapacityPurchaseDialogOfferKey(offerKey || "");
  };

  const handleResourceAdded = (resource, clientResource) => {
    if (!resource?.key) {
      return;
    }

    setAddedClientResources((previous) => {
      const next = new Map(previous);
      next.set(resource.key, normalizeAddedClientResource(resource, clientResource));
      return next;
    });
  };

  const handleResourcePurchased = (resource, clientResource) => {
    handleResourceAdded(resource, clientResource);
    loadCatalog();
  };

  const handleRuntimeCapacityPurchased = () => {
    loadCatalog();
  };

  const capacityPurchaseDialogActionVerb = getRuntimeCapacityActionVerb({
    clientProductOffers: catalog.clientProductOffers || [],
    offerKey: capacityPurchaseDialogOfferKey
  });

  const getResourceConfigurations = (resource) => {
    if (!resource?.key) return [];
    const clientResource = ownedClientResourceLookup.get(resource.key);
    const clientResourceId = clientResource?._id || clientResource?.id || clientResource?.UniqueId || "";
    return (catalog.resourceConfigurations || [])
      .filter((configuration) =>
        (!clientResourceId || configuration.clientResourceId === clientResourceId)
        && configuration.productResourceKey === resource.key)
      .sort((left, right) =>
        String(right.updatedDate || right.createdDate || "").localeCompare(String(left.updatedDate || left.createdDate || ""))
      );
  };

  useEffect(() => {
    if (!publicMode) {
      return undefined;
    }

    const image = new Image();
    image.onload = () => setHeroImageLoaded(true);
    image.src = HeroBg;

    if (image.complete) {
      setHeroImageLoaded(true);
    }

    return () => {
      image.onload = null;
    };
  }, [publicMode]);

  const shellClass = publicMode
    ? "homepage marketplace-page marketplace-page--public"
    : "marketplace-page marketplace-page--secure vertical-scrollable";

  return (
    <div className={shellClass}>
      {publicMode && <PublicSiteHeader />}
      <main className="marketplace-surface" aria-label={modeLabel[mode]}>
        {notFound ? (
          <MarketplaceNotFound basePath={basePath} />
        ) : selectedResource ? (
          <ResourceDetail
            resource={selectedResource}
            product={selectedProduct}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            backPath={selectedProduct ? productPath(selectedProduct) : basePath}
            publicMode={publicMode}
            onAddResource={handleAddResource}
            onConfigureResource={handleConfigureResource}
            ownedClientResource={selectedResource?.key ? ownedClientResourceLookup.get(selectedResource.key) : null}
            ownedActionMode={ownedActionMode}
            hasActiveGrant={hasActiveAllowance(selectedResource, catalog)}
            hasActiveConfiguration={hasActiveResourceConfiguration(selectedResource, catalog, ownedClientResourceLookup)}
            isMarketplaceUnlimited={catalog.isMarketplaceUnlimited}
            onContact={() => openMarketplaceSalesSupport({ product: selectedProduct, resource: selectedResource })}
          />
        ) : selectedProduct ? (
          <ProductDetail
            product={selectedProduct}
            resources={getProductResources(selectedProduct.key, resources)}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            backPath={basePath}
            resourcePath={resourcePath}
            publicMode={publicMode}
            ownedClientResourceLookup={ownedClientResourceLookup}
            onAddResource={handleAddResource}
            onConfigureResource={handleConfigureResource}
            onAddRuntimeCapacityOffer={handleAddRuntimeCapacityOffer}
            onContact={() => openMarketplaceSalesSupport({ product: selectedProduct })}
            catalog={catalog}
          />
        ) : (
          <MarketplaceHome
            publicMode={publicMode}
            query={query}
            setQuery={setQuery}
            selectedCategory={selectedCategory}
            setSelectedCategory={setSelectedCategory}
            featuredResources={featuredResources}
            filteredProducts={filteredProducts}
            filteredResources={filteredResources}
            productPath={productPath}
            resourcePath={resourcePath}
            products={products}
            visibleResources={visibleResources}
            heroImageLoaded={heroImageLoaded}
            ownedClientResourceLookup={ownedClientResourceLookup}
            onAddResource={handleAddResource}
            onConfigureResource={handleConfigureResource}
            isMyResourcesFilter={isMyResourcesFilter}
            catalog={catalog}
          />
        )}
        {!publicMode && (
          <MarketplaceAllowancePurchaseDialog
            open={Boolean(purchaseDialogResource)}
            resource={purchaseDialogResource}
            product={purchaseDialogResource ? getProduct(purchaseDialogResource.productKey, products) : null}
            environmentKey={catalog.environmentKey}
            clientProductOffers={catalog.clientProductOffers || []}
            initialOfferKey={purchaseDialogOfferKey}
            actionVerb="Add"
            isSelfHostedResourceMode={catalog.isSelfHostedResourceMode}
            onClose={() => {
              setPurchaseDialogResource(null);
              setPurchaseDialogOfferKey("");
            }}
            onPurchased={handleResourcePurchased}
          />
        )}
        {!publicMode && (
          <MarketplaceConfigurationChooserDialog
            open={Boolean(configureDialogResource)}
            resource={configureDialogResource}
            clientResource={configureDialogResource?.key ? ownedClientResourceLookup.get(configureDialogResource.key) : null}
            configurations={getResourceConfigurations(configureDialogResource)}
            onClose={() => setConfigureDialogResource(null)}
          />
        )}
        {!publicMode && (
          <MarketplaceRuntimeCapacityPurchaseDialog
            open={Boolean(capacityPurchaseDialogProduct)}
            product={capacityPurchaseDialogProduct}
            offers={capacityPurchaseDialogProduct?.offers || []}
            environmentKey={catalog.environmentKey}
            clientProductOffers={catalog.clientProductOffers || []}
            initialOfferKey={capacityPurchaseDialogOfferKey}
            actionVerb={capacityPurchaseDialogActionVerb}
            isSelfHostedResourceMode={catalog.isSelfHostedResourceMode}
            onClose={() => {
              setCapacityPurchaseDialogProduct(null);
              setCapacityPurchaseDialogOfferKey("");
            }}
            onPurchased={handleRuntimeCapacityPurchased}
          />
        )}
      </main>
    </div>
  );
};

const isMarketplaceApiAvailable = async () => {
  const apiBase = window?.configData?.API_URIS?.CONVOKE;
  if (!apiBase) {
    return false;
  }

  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), 1500);

  try {
    const apiUrl = new URL(apiBase);
    const response = await fetch(`${apiUrl.origin}/api/ping`, {
      method: "GET",
      signal: controller.signal
    });
    return response.ok;
  } catch {
    return false;
  } finally {
    window.clearTimeout(timeoutId);
  }
};

const MarketplaceHome = ({
  publicMode,
  query,
  setQuery,
  selectedCategory,
  setSelectedCategory,
  featuredResources,
  filteredProducts,
  filteredResources,
  productPath,
  resourcePath,
  products,
  visibleResources,
  heroImageLoaded,
  ownedClientResourceLookup,
  onAddResource,
  onConfigureResource,
  isMyResourcesFilter,
  catalog
}) => (
  <>
    <div
      className={publicMode
        ? `marketplace-public-hero ${heroImageLoaded ? "marketplace-public-hero--loaded" : ""}`
        : "marketplace-standard-lead"}
      style={publicMode
        ? {
            "--marketplace-hero-preview-image": `url(${HeroBgLow})`,
            "--marketplace-hero-image": `url(${HeroBg})`
          }
        : undefined}
    >
      <section className="marketplace-intro">
        <div className="marketplace-intro-copy">
          <div className="marketplace-eyebrow">Product marketplace</div>
          <h1>{publicMode ? "Explore Convoke products" : "Marketplace"}</h1>
          <p>
            Search integrations, destinations, analytics, EDI, AI, automation, diagnostics, and app experiences that can
            be added to a Convoke workspace.
          </p>
        </div>
        <div className="marketplace-intro-panel" aria-label="Marketplace summary">
          <div className="marketplace-summary-bubble">
            <div className="marketplace-summary-stat">
              <strong>{marketplaceCategories.length}</strong>
              <span>Categories</span>
            </div>
            <div className="marketplace-summary-stat">
              <strong>{products.length}</strong>
              <span>Products</span>
            </div>
            <div className="marketplace-summary-stat">
              <strong>{visibleResources.length}</strong>
              <span>Resources</span>
            </div>
          </div>
        </div>
      </section>

      <section className="marketplace-search-panel" aria-label="Search marketplace">
        <label className="marketplace-search">
          <span className="marketplace-search-icon" aria-hidden="true"><SearchIcon /></span>
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search products and resources"
            type="search"
          />
        </label>
        <CategoryChips
          selectedCategory={selectedCategory}
          setSelectedCategory={setSelectedCategory}
          showMyResources={!publicMode}
        />
      </section>
    </div>

    {!query && selectedCategory === MARKETPLACE_FILTER_ALL && (
      <section className="marketplace-section">
        <SectionHeader title="Featured" description="Common starting points for client marketplace conversations." />
        <div className="marketplace-card-grid marketplace-card-grid--featured">
          {featuredResources.map((resource) => (
            <ResourceCard
              key={resource.key}
              resource={resource}
              product={getProduct(resource.productKey, products)}
              publicMode={publicMode}
              detailPath={resourcePath(resource)}
              ownedClientResource={ownedClientResourceLookup?.get(resource.key)}
              onAddResource={onAddResource}
              onConfigureResource={onConfigureResource}
              hasActiveGrant={hasActiveAllowance(resource, catalog)}
              hasActiveConfiguration={hasActiveResourceConfiguration(resource, catalog, ownedClientResourceLookup)}
              isMarketplaceUnlimited={catalog?.isMarketplaceUnlimited}
            />
          ))}
        </div>
      </section>
    )}

    {isMyResourcesFilter ? (
      <section className="marketplace-section">
        <SectionHeader
          title={query ? `My Resources matching "${query}"` : "My Resources"}
          description={`${filteredResources.length} resources are currently added or purchased in this workspace.`}
        />
        <div className="marketplace-card-grid">
          {filteredResources.map((resource) => (
            <ResourceCard
              key={resource.key}
              resource={resource}
              product={getProduct(resource.productKey, products)}
              publicMode={publicMode}
              detailPath={resourcePath(resource)}
              ownedClientResource={ownedClientResourceLookup?.get(resource.key)}
              onAddResource={onAddResource}
              onConfigureResource={onConfigureResource}
              hasActiveGrant={hasActiveAllowance(resource, catalog)}
              hasActiveConfiguration={hasActiveResourceConfiguration(resource, catalog, ownedClientResourceLookup)}
              isMarketplaceUnlimited={catalog?.isMarketplaceUnlimited}
            />
          ))}
        </div>
        {filteredResources.length === 0 && (
          <EmptyState onClear={() => { setQuery(""); setSelectedCategory(MARKETPLACE_FILTER_ALL); }} />
        )}
      </section>
    ) : (
      <section className="marketplace-section">
        <SectionHeader
          title={query ? `Results for "${query}"` : "Browse products"}
          description={`${filteredProducts.length} products and ${filteredResources.length} resources match the current view.`}
        />
        <div className="marketplace-card-grid">
          {filteredProducts.map((product) => (
            <ProductCard
              key={product.key}
              product={product}
              resourceCount={getProductResources(product.key, visibleResources).length}
              planCount={(product.offers || []).length}
              detailPath={productPath(product)}
            />
          ))}
        </div>
        {filteredProducts.length === 0 && (
          <EmptyState onClear={() => { setQuery(""); setSelectedCategory(MARKETPLACE_FILTER_ALL); }} />
        )}
      </section>
    )}

    {query && !isMyResourcesFilter && filteredResources.length > 0 && (
      <section className="marketplace-section">
        <SectionHeader title="Matching resources" description="Concrete providers and runtime resources under products." />
        <div className="marketplace-resource-list">
          {filteredResources.map((resource) => (
            <ResourceListItem
              key={resource.key}
              resource={resource}
              product={getProduct(resource.productKey, products)}
              publicMode={publicMode}
              detailPath={resourcePath(resource)}
            />
          ))}
        </div>
      </section>
    )}

    {publicMode && (
      <section className="marketplace-public-cta" aria-label="Public marketplace call to action">
        <div>
          <h2>Ready to add a product to your workspace?</h2>
          <p>Sign in to continue in Convoke, or contact us to discuss access and setup.</p>
        </div>
        <div className="marketplace-actions">
          <a className="marketplace-button marketplace-button--primary" href="/login/">Sign in</a>
          <a className="marketplace-button marketplace-button--secondary" href="mailto:info@ConvokeDX.com?subject=Convoke%20Marketplace%20information">Contact</a>
        </div>
      </section>
    )}
  </>
);

const SearchIcon = () => (
  <svg viewBox="0 0 24 24" focusable="false" aria-hidden="true">
    <circle cx="11" cy="11" r="7" />
    <path d="m16.5 16.5 4 4" />
  </svg>
);

const ProductDetail = ({
  product,
  resources,
  activeTab,
  setActiveTab,
  backPath,
  resourcePath,
  publicMode,
  ownedClientResourceLookup,
  onAddResource,
  onConfigureResource,
  onAddRuntimeCapacityOffer,
  onContact,
  catalog
}) => {
  const category = getCategory(product.categoryKey);
  const productOffers = product.offers || [];
  const hasProductOffers = productOffers.length > 0;
  const optionsLabel = hasProductOffers && resources.length === 0 ? "Plans" : "Resources";
  const resourceGroups = getMarketplaceResourceGroups(resources);

  const renderResourceCard = (resource) => (
    <ResourceCard
      key={resource.key}
      resource={resource}
      product={product}
      publicMode={publicMode}
      detailPath={resourcePath(resource)}
      ownedClientResource={ownedClientResourceLookup?.get(resource.key)}
      onAddResource={onAddResource}
      onConfigureResource={onConfigureResource}
      hasActiveGrant={hasActiveAllowance(resource, catalog)}
      hasActiveConfiguration={hasActiveResourceConfiguration(resource, catalog, ownedClientResourceLookup)}
      isMarketplaceUnlimited={catalog?.isMarketplaceUnlimited}
    />
  );

  return (
    <>
      <Link className="marketplace-back" to={backPath}>&lt;&lt; Back to Marketplace</Link>
      <section className="marketplace-detail-hero">
        <ProductIcon label={product.name} categoryKey={product.categoryKey} iconKey={product.Icon} iconPath={product.IconPath} />
        <div className="marketplace-detail-copy">
          <span className="marketplace-eyebrow">{category?.label}</span>
          <h1>{product.name}</h1>
          <p>{product.summary}</p>
          <div className="marketplace-actions">
            <button type="button" className="marketplace-button marketplace-button--primary" onClick={() => setActiveTab("options")}>
              {optionsLabel === "Plans" ? "View plans" : "View resources"}
            </button>
            {publicMode ? (
              <a className="marketplace-button marketplace-button--secondary" href="mailto:info@ConvokeDX.com?subject=Convoke%20Marketplace%20information">
                Contact
              </a>
            ) : (
              <button type="button" className="marketplace-button marketplace-button--secondary" onClick={onContact}>
                Contact
              </button>
            )}
          </div>
        </div>
      </section>
      <DetailTabs activeTab={activeTab} setActiveTab={setActiveTab} optionsLabel={optionsLabel} />
      <section className="marketplace-detail-body">
        {activeTab === "overview" && (
          <div className="marketplace-detail-grid">
            <article>
              <h2>Overview</h2>
              <MarketingRichText html={product.MarketingOverviewHtml} fallback={product.summary} />
            </article>
            <article>
              <h2>Included capabilities</h2>
              <div className="marketplace-chip-list">
                {product.modules.map((moduleName) => <span key={moduleName}>{formatTagLabel(moduleName)}</span>)}
              </div>
            </article>
          </div>
        )}
        {activeTab === "options" && (
          hasProductOffers ? (
            <div className="marketplace-card-grid">
              {productOffers.map((offer) => (
              <RuntimeCapacityOfferCard
                key={offer.key}
                offer={offer}
                product={product}
                publicMode={publicMode}
                onAddRuntimeCapacityOffer={onAddRuntimeCapacityOffer}
                clientProductOffers={catalog?.clientProductOffers || []}
                isSelfHostedResourceMode={catalog?.isSelfHostedResourceMode}
              />
              ))}
            </div>
          ) : resourceGroups.length > 0 ? (
            <div className="marketplace-resource-groups">
              {resourceGroups.map((group) => (
                <section className="marketplace-resource-group" key={group.key}>
                  <header className="marketplace-resource-group__header">
                    <h2>{group.label}</h2>
                    <p>{group.description}</p>
                  </header>
                  <div className="marketplace-card-grid">
                    {group.resources.map(renderResourceCard)}
                  </div>
                </section>
              ))}
            </div>
          ) : (
            <div className="marketplace-card-grid">
              {resources.map(renderResourceCard)}
              {resources.length === 0 ? (
              <InfoPanel
                title="Plans"
                body="No active plans are currently available for this product."
              />
              ) : null}
            </div>
          )
        )}
      </section>
    </>
  );
};

const ResourceDetail = ({
  resource,
  product,
  activeTab,
  setActiveTab,
  backPath,
  publicMode,
  onAddResource,
  onConfigureResource,
  ownedClientResource,
  ownedActionMode,
  hasActiveGrant,
  hasActiveConfiguration,
  isMarketplaceUnlimited,
  onContact
}) => {
  const canPurchaseAdditionalInstance = !resource.isSingleInstance || !ownedClientResource;
  const canStartAdd = canPurchaseAdditionalInstance
    && ["unlimited", "unlocked", "available"].includes(resource.availability);
  const hasPurchasableOffer = (resource.offers || []).some((offer) => offer.offerKind !== 2);
  const canAddResource = !resource.isSystemManaged && !publicMode && canStartAdd && (hasActiveGrant || hasPurchasableOffer);
  const hasUnlimitedEntitlement = !publicMode && Boolean(
    hasActiveGrant
    && hasUnlimitedResourceEntitlement(ownedClientResource, isMarketplaceUnlimited)
  );
  const canBuyAllowance = !publicMode && canStartAdd && hasPurchasableOffer && !isMarketplaceUnlimited;
  const effectiveActiveTab = hasUnlimitedEntitlement ? "overview" : activeTab;

  return (
  <>
    <Link className="marketplace-back" to={backPath}>&lt;&lt; Back to {product?.name}</Link>
    <section className="marketplace-detail-hero">
      <div className="marketplace-detail-corner-status">
        <ResourceMaturityBadge value={resource.maturityStatus} />
      </div>
      <ProductIcon
        label={resource.name}
        categoryKey={product?.categoryKey}
        iconKey={resource.Icon || product?.Icon}
        iconPath={resource.IconPath || product?.IconPath}
      />
      <div className="marketplace-detail-copy">
        <span className="marketplace-eyebrow">{product?.name}</span>
        <h1>{resource.name}</h1>
        <p>{resource.summary}</p>
        <div className="marketplace-badge-row">
          <AvailabilityBadge availability={resource.availability} publicMode={publicMode} />
          {ownedClientResource ? <span className="marketplace-badge marketplace-badge--owned">{ownedActionMode === "purchased" ? "Purchased" : "Added"}</span> : null}
          <span className="marketplace-badge marketplace-badge--neutral">Setup required</span>
          <span className="marketplace-badge marketplace-badge--neutral">
            {getPricingLabel(resource.availability, publicMode)}
          </span>
        </div>
        <div className="marketplace-actions">
          {publicMode ? (
            <>
              <a className="marketplace-button marketplace-button--primary" href="/login/">Sign in to add</a>
              <a className="marketplace-button marketplace-button--secondary" href="mailto:info@ConvokeDX.com?subject=Convoke%20Marketplace%20information">Contact</a>
            </>
          ) : (
            <>
              {resource.isSystemManaged && !ownedClientResource ? (
                <span className="marketplace-button marketplace-button--secondary">Created automatically</span>
              ) : hasUnlimitedEntitlement ? (
                <button
                  type="button"
                  className="marketplace-button marketplace-button--primary"
                  onClick={() => ownedClientResource
                    ? onConfigureResource?.(resource, { startNew: !hasActiveConfiguration })
                    : onAddResource?.(resource)}
                >
                  {ownedClientResource && hasActiveConfiguration ? "Configure" : "Add"}
                </button>
              ) : ownedClientResource && (hasActiveGrant || resource.isSingleInstance) ? (
                <button
                  type="button"
                  className="marketplace-button marketplace-button--primary"
                  onClick={() => onConfigureResource?.(resource)}
                >
                  Configure
                </button>
              ) : (
                <button
                  type="button"
                  className="marketplace-button marketplace-button--primary"
                  onClick={canAddResource ? () => onAddResource?.(resource) : undefined}
                  disabled={!canAddResource}
                >
                  {isMarketplaceUnlimited ? "Select" : "Buy"}
                </button>
              )}
              {!hasUnlimitedEntitlement && ownedClientResource && hasActiveGrant && !resource.isSingleInstance ? (
                <button
                  type="button"
                  className="marketplace-button marketplace-button--secondary"
                  onClick={canBuyAllowance ? () => onAddResource?.(resource, "", { forcePurchase: true }) : undefined}
                  disabled={!canBuyAllowance}
                >
                  Add Allowance
                </button>
              ) : null}
              <button type="button" className="marketplace-button marketplace-button--secondary" onClick={onContact}>Contact</button>
            </>
          )}
        </div>
      </div>
    </section>
    <DetailTabs
      activeTab={effectiveActiveTab}
      setActiveTab={setActiveTab}
      optionsLabel="Plans"
      hideOptions={hasUnlimitedEntitlement}
    />
    <section className="marketplace-detail-body">
      {effectiveActiveTab === "overview" && (
        <div className="marketplace-detail-grid">
          <article>
            <h2>Overview</h2>
            <MarketingRichText html={resource.MarketingOverviewHtml} fallback={resource.summary} />
          </article>
          <article>
            <h2>At a glance</h2>
            <dl className="marketplace-facts">
              <div>
                <dt>Category</dt>
                <dd>{getCategory(product?.categoryKey)?.label}</dd>
              </div>
              <div>
                <dt>Product</dt>
                <dd>{product?.name}</dd>
              </div>
              <div>
                <dt>Availability</dt>
                <dd>{getAvailabilityLabel(resource.availability, publicMode)}</dd>
              </div>
              <div>
                <dt>Pricing</dt>
                <dd>{getPricingLabel(resource.availability, publicMode)}</dd>
              </div>
            </dl>
          </article>
        </div>
      )}
      {effectiveActiveTab === "options" && (
        <div className="marketplace-card-grid">
          {(resource.offers || []).map((offer) => (
            <OfferCard
              key={offer.key}
              offer={offer}
              resource={resource}
              publicMode={publicMode}
              canStartAdd={canStartAdd}
              onAddResource={onAddResource}
            />
          ))}
          {(resource.offers || []).length === 0 && (
            <InfoPanel
              title="Plans and allowance"
              body="No active offer is currently placed on this resource. Contact Convoke for package options."
            />
          )}
        </div>
      )}
    </section>
  </>
  );
};

const getPricingLabel = (availability, publicMode = false) => {
  if (publicMode) {
    return "Contact for pricing";
  }

  if (availability === "unlimited") return "Included with unlimited access";
  if (availability === "unlocked") return "Included in purchased package";
  return "Pricing pending review";
};

const getOwnedActionLabel = (clientResource) => {
  if (!clientResource) {
    return "";
  }

  return "Configure";
};

const hasActiveAllowance = (resource, catalog) => {
  if (!resource?.key || !catalog) {
    return false;
  }

  if (catalog.isMarketplaceUnlimited) {
    return true;
  }

  return (catalog.clientProductOffers || []).some((offer) =>
    (offer.resourceGrants || []).some((grant) =>
      String(grant.productResourceKey || "").toLowerCase() === String(resource.key || "").toLowerCase()
    )
  );
};

const getResourceCardActionLabel = (resource, publicMode, ownedClientResource, hasActiveGrant) => {
  if (publicMode) {
    return "Marketing details";
  }

  if (resource.isSystemManaged && !ownedClientResource) {
    return "Created automatically";
  }

  if (resource.isSingleInstance && ownedClientResource) {
    return "Single instance";
  }

  return (ownedClientResource && hasActiveGrant ? getOwnedActionLabel(ownedClientResource) : "")
    || getPrimaryActionLabel(resource.availability, false);
};

const CategoryChips = ({ selectedCategory, setSelectedCategory, showMyResources }) => (
  <div className="marketplace-category-chips" aria-label="Marketplace filters">
    <button
      type="button"
      className={selectedCategory === MARKETPLACE_FILTER_ALL ? "active" : ""}
      onClick={() => setSelectedCategory(MARKETPLACE_FILTER_ALL)}
    >
      All
    </button>
    {showMyResources && (
      <button
        type="button"
        className={selectedCategory === MARKETPLACE_FILTER_MY_RESOURCES ? "active" : ""}
        onClick={() => setSelectedCategory(MARKETPLACE_FILTER_MY_RESOURCES)}
      >
        My Resources
      </button>
    )}
    {marketplaceCategories.map((category) => (
      <button
        type="button"
        key={category.key}
        className={selectedCategory === category.key ? "active" : ""}
        onClick={() => setSelectedCategory(category.key)}
      >
        {category.label}
      </button>
    ))}
  </div>
);

const ProductCard = ({ product, resourceCount, planCount = 0, detailPath }) => {
  const category = getCategory(product.categoryKey);
  const navigate = useNavigate();
  const showsPlans = planCount > 0 && resourceCount === 0;
  const resourcesPath = withMarketplaceTab(detailPath, showsPlans ? "plans" : "resources");
  const optionCountLabel = showsPlans
    ? `${planCount} ${planCount === 1 ? "plan" : "plans"}`
    : `${resourceCount} ${resourceCount === 1 ? "resource" : "resources"}`;
  const openProduct = () => navigate(detailPath);
  const shouldIgnoreCardActivation = (event) =>
    Boolean(event.target.closest("a, button, input, select, textarea, label"));
  const handleCardClick = (event) => {
    if (!shouldIgnoreCardActivation(event)) openProduct();
  };
  const handleCardKeyDown = (event) => {
    if (shouldIgnoreCardActivation(event) || (event.key !== "Enter" && event.key !== " ")) return;
    event.preventDefault();
    openProduct();
  };

  return (
    <article
      className="marketplace-card marketplace-card--clickable"
      role="link"
      tabIndex={0}
      aria-label={`View product ${product.name}`}
      onClick={handleCardClick}
      onKeyDown={handleCardKeyDown}
    >
      <div className="marketplace-card-header">
        <ProductIcon label={product.name} categoryKey={product.categoryKey} iconKey={product.Icon} iconPath={product.IconPath} />
        <div>
          <span className="marketplace-eyebrow">{category?.label}</span>
          <h2>{product.name}</h2>
        </div>
      </div>
      <p>{product.summary}</p>
      <div className="marketplace-chip-list">
        {product.modules.slice(0, 3).map((moduleName) => <span key={moduleName}>{formatTagLabel(moduleName)}</span>)}
      </div>
      <div className="marketplace-card-footer">
        <Link className="marketplace-text-button" to={resourcesPath}>{optionCountLabel}</Link>
        <Link className="marketplace-text-button" to={detailPath}>View product</Link>
      </div>
    </article>
  );
};

const getRuntimeCapacityGrant = (offer) => {
  const grants = offer?.capacityGrants || [];
  return grants.find((grant) => grant.isActive !== false) || grants[0] || null;
};

const getRuntimeCapacityPoolLabel = (poolType) => {
  switch (Number(poolType || 0)) {
    case 1:
      return "Small pool";
    case 2:
      return "Medium pool";
    case 3:
      return "Large pool";
    default:
      return "Any active pool";
  }
};

const buildRuntimeCapacityGrantSummary = (offer) => {
  const grant = getRuntimeCapacityGrant(offer);
  if (!grant) {
    return "Runtime capacity grant";
  }
  if (grant.isUnlimited) {
    return "Unlimited runtime capacity";
  }
  const maxConcurrentRuns = Math.max(1, Number(grant.maxConcurrentRuns || 1));
  return `${getRuntimeCapacityPoolLabel(grant.poolType)}; ${maxConcurrentRuns} active ${maxConcurrentRuns === 1 ? "run" : "runs"}`;
};

const RuntimeCapacityOfferCard = ({
  offer,
  product,
  publicMode,
  onAddRuntimeCapacityOffer,
  clientProductOffers = [],
  isSelfHostedResourceMode = false
}) => {
  const grant = getRuntimeCapacityGrant(offer);
  const pricingDisplay = buildOfferPricingDisplay(offer);
  const canAddOffer = !publicMode && offer.offerKind === 4;
  const isOwnedUnlimitedOffer = grant?.isUnlimited === true && hasRuntimeCapacityClientOffer(clientProductOffers, offer.key);
  const actionVerb = isSelfHostedResourceMode
    ? "Select"
    : getRuntimeCapacityActionVerb({
        clientProductOffers,
        offerKey: offer.key,
        publicMode
      });

  return (
    <article className="marketplace-card marketplace-card--offer marketplace-card--runtime-capacity-offer">
      <div className="marketplace-card-header">
        <div>
          <span className="marketplace-eyebrow">{offer.sku || offer.key}</span>
          <h2>{offer.name}</h2>
        </div>
      </div>
      <p>{offer.priceSummary || "Runtime capacity grant for scheduled and pool-backed execution."}</p>
      <div className="marketplace-offer-pricing" aria-label="Plan pricing">
        <span>Pricing</span>
        <strong>{pricingDisplay.primary}</strong>
        <small>{pricingDisplay.detail}</small>
      </div>
      <div className="marketplace-badge-row">
        {offer.reviewRequired ? <span className="marketplace-badge marketplace-badge--review">Review required</span> : null}
        {offer.customPricing ? <span className="marketplace-badge marketplace-badge--admin">Custom</span> : null}
        <span className="marketplace-badge marketplace-badge--neutral">{buildRuntimeCapacityGrantSummary(offer)}</span>
        {grant?.isUnlimited ? <span className="marketplace-badge marketplace-badge--review">Unlimited</span> : null}
        {isOwnedUnlimitedOffer ? <span className="marketplace-badge marketplace-badge--owned">Purchased</span> : null}
      </div>
      {!publicMode && (!isOwnedUnlimitedOffer || isSelfHostedResourceMode) && (
        <div className="marketplace-card-footer">
          <ButtonControl
            type="ok-small"
            onClick={() => onAddRuntimeCapacityOffer?.(product, offer.key)}
            disabled={!canAddOffer}
          >
            {actionVerb}
          </ButtonControl>
        </div>
      )}
    </article>
  );
};

const ResourceCard = ({
  resource,
  product,
  publicMode,
  detailPath,
  ownedClientResource,
  onAddResource,
  onConfigureResource,
  hasActiveGrant,
  hasActiveConfiguration,
  isMarketplaceUnlimited
}) => {
  const actionLabel = getResourceCardActionLabel(resource, publicMode, ownedClientResource, hasActiveGrant);
  const hasPurchasableOffer = (resource.offers || []).some((offer) => offer.offerKind !== 2);
  const canAddResource = !resource.isSystemManaged
    && !publicMode
    && ["unlimited", "unlocked", "available"].includes(resource.availability)
    && (hasActiveGrant || hasPurchasableOffer)
    && (!resource.isSingleInstance || !ownedClientResource);
  const isResourceUnlimited = resource.isUnlimited || resource.availability === "unlimited";
  const hasUnlimitedEntitlement = !publicMode && Boolean(
    hasActiveGrant
    && hasUnlimitedResourceEntitlement(ownedClientResource, isMarketplaceUnlimited)
  );
  const canBuyAllowance = ownedClientResource
    && hasActiveGrant
    && hasPurchasableOffer
    && !isMarketplaceUnlimited
    && !resource.isSingleInstance;
  const navigate = useNavigate();

  const openDetails = () => {
    if (detailPath) {
      navigate(detailPath);
    }
  };

  const shouldIgnoreCardActivation = (event) =>
    Boolean(event.target.closest("a, button, input, select, textarea, label"));

  const handleCardClick = (event) => {
    if (shouldIgnoreCardActivation(event)) {
      return;
    }

    openDetails();
  };

  const handleCardKeyDown = (event) => {
    if (shouldIgnoreCardActivation(event)) {
      return;
    }

    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      openDetails();
    }
  };

  return (
    <article
      className={`marketplace-card marketplace-card--resource marketplace-card--clickable${ownedClientResource ? " marketplace-card--owned" : ""}`}
      role="link"
      tabIndex={0}
      aria-label={`${publicMode ? "Learn more about" : "View details for"} ${resource.name}`}
      onClick={handleCardClick}
      onKeyDown={handleCardKeyDown}
    >
      <div className="marketplace-card-corner-status">
        <ResourceMaturityBadge value={resource.maturityStatus} />
        {ownedClientResource ? (
          <span className="marketplace-card-owned-star" role="img" aria-label="Added resource" title="Added resource">
            <IconControl iconKey="favorite" title="Added resource" width="1rem" height="1rem" />
          </span>
        ) : null}
      </div>
      <div className="marketplace-card-header">
        <ProductIcon
          label={resource.name}
          categoryKey={product?.categoryKey}
          iconKey={resource.Icon || product?.Icon}
          iconPath={resource.IconPath || product?.IconPath}
        />
        <div>
          <span className="marketplace-eyebrow">{product?.name}</span>
          <h2>{resource.name}</h2>
        </div>
      </div>
      <p>{resource.summary}</p>
      {resource.isCatalogGroupRecommendation ? (
        <div className="marketplace-badge-row">
          <span className="marketplace-badge marketplace-badge--owned">Recommended</span>
        </div>
      ) : null}
      {isResourceUnlimited ? (
        <div className="marketplace-badge-row">
          <AvailabilityBadge availability="unlimited" publicMode={publicMode} />
        </div>
      ) : null}
      <div className="marketplace-card-footer">
        {hasUnlimitedEntitlement ? (
          <button
            type="button"
            className="marketplace-text-button"
            onClick={() => ownedClientResource
              ? onConfigureResource?.(resource, { startNew: !hasActiveConfiguration })
              : onAddResource?.(resource)}
          >
            {ownedClientResource && hasActiveConfiguration ? "Configure" : "Add"}
          </button>
        ) : (
          <>
            {canBuyAllowance ? (
              <button type="button" className="marketplace-text-button" onClick={() => onAddResource?.(resource, "", { forcePurchase: true })}>
                Add Allowance
              </button>
            ) : canAddResource ? (
              <button type="button" className="marketplace-text-button" onClick={() => onAddResource?.(resource)}>
                Buy
              </button>
            ) : (
              <span>{actionLabel}</span>
            )}
            {ownedClientResource && (hasActiveGrant || resource.isSingleInstance) ? (
              <button type="button" className="marketplace-text-button" onClick={() => onConfigureResource?.(resource)}>
                Configure
              </button>
            ) : (
              <Link className="marketplace-text-button" to={detailPath}>
                {publicMode ? "Learn more" : "View details"}
              </Link>
            )}
          </>
        )}
      </div>
    </article>
  );
};

const formatConfigurationAllowance = (count, suffix = "") => {
  const normalizedCount = Number(count || 0);
  const label = normalizedCount === 1 ? "configuration" : "configurations";
  return `${normalizedCount} ${label}${suffix}`;
};

const buildOfferGrantSummaries = (primaryGrants, offerKind) => {
  if (primaryGrants.length === 0) {
    return [offerKind === 3 ? "Unlimited eligible resources" : "Manual package"];
  }

  if (primaryGrants.length === 1) {
    return [formatConfigurationAllowance(primaryGrants[0].allowedResourceConfigurationCount)];
  }

  const allowanceCounts = primaryGrants.map((grant) => Number(grant.allowedResourceConfigurationCount || 0));
  const firstAllowanceCount = allowanceCounts[0];
  const sameAllowance = allowanceCounts.every((count) => count === firstAllowanceCount);

  return [
    `${primaryGrants.length} resources included`,
    sameAllowance
      ? formatConfigurationAllowance(firstAllowanceCount, " each")
      : "Mixed configuration allowances"
  ];
};

const formatOfferCurrency = (amount, currencyCode = "USD") => {
  const normalizedAmount = Number(amount || 0);
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: currencyCode || "USD",
    maximumFractionDigits: normalizedAmount % 1 === 0 ? 0 : 2
  }).format(normalizedAmount);
};

const getOfferBillingPeriodLabel = (billingPeriod) => {
  switch (Number(billingPeriod || 0)) {
    case 1:
      return "month";
    case 2:
      return "year";
    case 3:
      return "one time";
    case 4:
      return "manual";
    default:
      return "period";
  }
};

const buildOfferPricingDisplay = (offer) => {
  if (offer.reviewRequired) {
    return {
      primary: "Review required",
      detail: "Pricing is finalized before activation."
    };
  }

  if (offer.customPricing) {
    return {
      primary: "Custom pricing",
      detail: "Commercial terms are managed on this Product Offer."
    };
  }

  const baseAmount = Number(offer.baseAmount || 0);
  const unitRate = Number(offer.unitRate || 0);
  const billingPeriodLabel = getOfferBillingPeriodLabel(offer.billingPeriod);

  if (baseAmount > 0) {
    return {
      primary: `${formatOfferCurrency(baseAmount, offer.currencyCode)} / ${billingPeriodLabel}`,
      detail: unitRate > 0
        ? "Base estimate, with usage billed after included plan usage."
        : "Base package estimate."
    };
  }

  if (unitRate > 0) {
    return {
      primary: `${formatOfferCurrency(unitRate, offer.currencyCode)} per ${offer.unitLabel || "unit"}`,
      detail: "Usage-based estimate for this Product Offer."
    };
  }

  return {
    primary: "Pricing managed on offer",
    detail: "No base price is set for this plan yet."
  };
};

const buildOfferDescription = (offer, primaryGrants) => {
  if (offer.offerKind === 3) {
    return "Marketplace-wide package for eligible active resources.";
  }

  if (offer.offerKind === 2) {
    return "Manual license charge package managed by the commercial team.";
  }

  if (primaryGrants.length > 1) {
    return "Bundle package that unlocks multiple Product Resources under one SKU.";
  }

  const primaryGrant = primaryGrants[0];
  if (Number(primaryGrant?.allowedResourceConfigurationCount || 0) > 1) {
    return "Scale package for adding more configured resource instances.";
  }

  return "Single-resource package for adding one configured resource instance.";
};

const OfferCard = ({
  offer,
  resource,
  publicMode,
  canStartAdd,
  onAddResource
}) => {
  const isManualLicense = offer.offerKind === 2;
  const canAddOffer = !publicMode
    && !isManualLicense
    && ["unlimited", "unlocked", "available"].includes(resource.availability);
  const canSelectOffer = !isManualLicense
    && !["review", "admin"].includes(resource.availability)
    && (canAddOffer || canStartAdd);
  const primaryGrants = (offer.resourceGrants || []).filter((grant) => grant.grantRole === 1);
  const grantSummaries = buildOfferGrantSummaries(primaryGrants, offer.offerKind);
  const hasIncludedUsage = (offer.resourceGrants || []).some((grant) => {
    const includedUsageUnits = Number(grant.includedUsageUnits || 0);
    return grant.usagePricingEnabled
      && includedUsageUnits > 0
      && includedUsageUnits < UNLIMITED_INCLUDED_USAGE_UNITS;
  });
  const usageSummary = hasIncludedUsage ? "Usage included in plan" : "";
  const pricingDisplay = buildOfferPricingDisplay(offer);
  const offerDescription = buildOfferDescription(offer, primaryGrants);
  const handleSelectOffer = () => {
    if (!canSelectOffer) return;
    onAddResource?.(resource, offer.key, { forcePurchase: true });
  };
  const handleOfferKeyDown = (event) => {
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    handleSelectOffer();
  };

  return (
    <article
      className={`marketplace-card marketplace-card--offer marketplace-card--selectable-offer${canSelectOffer ? "" : " marketplace-card--disabled-offer"}`}
      role={canSelectOffer ? "button" : undefined}
      tabIndex={canSelectOffer ? 0 : undefined}
      aria-disabled={canSelectOffer ? undefined : true}
      onClick={handleSelectOffer}
      onKeyDown={handleOfferKeyDown}
    >
      <div className="marketplace-card-header">
        <div>
          <span className="marketplace-eyebrow">{offer.sku}</span>
          <h2>{offer.name}</h2>
        </div>
      </div>
      <p>{offerDescription}</p>
      <div className="marketplace-offer-pricing" aria-label="Plan pricing">
        <span>Pricing</span>
        <strong>{pricingDisplay.primary}</strong>
        <small>{pricingDisplay.detail}</small>
      </div>
      <div className="marketplace-badge-row">
        {offer.reviewRequired ? <span className="marketplace-badge marketplace-badge--review">Review required</span> : null}
        {offer.customPricing ? <span className="marketplace-badge marketplace-badge--admin">Custom</span> : null}
        {grantSummaries.map((summary) => (
          <span key={summary} className="marketplace-badge marketplace-badge--neutral">{summary}</span>
        ))}
        {usageSummary ? <span className="marketplace-badge marketplace-badge--neutral">{usageSummary}</span> : null}
      </div>
    </article>
  );
};

const ResourceListItem = ({ resource, product, publicMode, detailPath }) => (
  <article className="marketplace-list-item">
    <ProductIcon
      label={resource.name}
      categoryKey={product?.categoryKey}
      iconKey={resource.Icon || product?.Icon}
      iconPath={resource.IconPath || product?.IconPath}
    />
    <div>
      <span className="marketplace-eyebrow">{product?.name}</span>
      <h2>{resource.name}</h2>
      <p>{resource.summary}</p>
      <AvailabilityBadge availability={resource.availability} publicMode={publicMode} />
    </div>
    <Link className="marketplace-button marketplace-button--secondary" to={detailPath}>
      View details
    </Link>
  </article>
);

const DetailTabs = ({ activeTab, setActiveTab, optionsLabel, hideOptions = false }) => {
  const tabs = hideOptions
    ? [["overview", "Overview"]]
    : [["overview", "Overview"], ["options", optionsLabel]];

  return (
    <div className="marketplace-tabs" role="tablist" aria-label="Marketplace detail tabs">
      {tabs.map(([key, label]) => (
        <button
          key={key}
          type="button"
          role="tab"
          aria-selected={activeTab === key}
          className={activeTab === key ? "active" : ""}
          onClick={() => setActiveTab(key)}
        >
          {label}
        </button>
      ))}
    </div>
  );
};

const AvailabilityBadge = ({ availability, publicMode }) => (
  <span className={`marketplace-badge marketplace-badge--${availability}`}>
    {getAvailabilityLabel(availability, publicMode)}
  </span>
);

const formatTagLabel = (value) => {
  const normalized = String(value ?? "")
    .replace(/[_-]+/g, " ")
    .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/\s+/g, " ")
    .trim();

  if (!normalized) {
    return "";
  }

  const acronyms = new Set(["ai", "api", "as2", "cdx", "dx", "edi", "mcp", "qa", "rte", "sql", "ui", "x12"]);
  const smallWords = new Set(["and", "for", "in", "of", "or", "the", "to"]);

  return normalized
    .split(" ")
    .map((word, index) => {
      const lower = word.toLowerCase();
      if (acronyms.has(lower)) {
        return lower.toUpperCase();
      }
      if (index > 0 && smallWords.has(lower)) {
        return lower;
      }
      return `${word.charAt(0).toUpperCase()}${word.slice(1)}`;
    })
    .join(" ");
};

const MarketingRichText = ({ html, fallback }) => {
  const sanitizedHtml = useMemo(() => sanitizeHtmlFragment(html), [html]);

  if (sanitizedHtml) {
    return (
      <div
        className="marketplace-rich-text"
        dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
      />
    );
  }

  return <p>{fallback}</p>;
};

const ProductIcon = ({ label, categoryKey, iconKey, iconPath }) => {
  const coreContext = useCoreContext();
  const resolvedIcon = iconKey ? coreContext?.Icons?.[iconKey.toLowerCase()] : null;
  const preferExplicitIconPath = /\.svg(?:\?.*)?$/i.test(String(iconPath ?? ""));
  const initials = String(label ?? "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();

  return (
    <span className={`marketplace-product-icon marketplace-product-icon--${categoryKey}`} aria-hidden="true">
      {preferExplicitIconPath ? (
        <img src={iconPath} alt="" />
      ) : resolvedIcon ? (
        <IconControl iconKey={iconKey} title={label} width="100%" height="100%" />
      ) : iconPath ? (
        <img src={iconPath} alt="" />
      ) : (
        initials || "MP"
      )}
    </span>
  );
};

const SectionHeader = ({ title, description }) => (
  <div className="marketplace-section-header">
    <div>
      <h2>{title}</h2>
      <p>{description}</p>
    </div>
  </div>
);

const InfoPanel = ({ title, body, children }) => (
  <article className="marketplace-info-panel">
    <h2>{title}</h2>
    {children ?? <p>{body}</p>}
  </article>
);

const MarketplaceNotFound = ({ basePath }) => (
  <div className="marketplace-empty">
    <h2>Marketplace page not found</h2>
    <p>The shared marketplace link does not match an available product or resource option.</p>
    <Link className="marketplace-button marketplace-button--secondary" to={basePath}>&lt;&lt; Back to Marketplace</Link>
  </div>
);

const EmptyState = ({ onClear }) => (
  <div className="marketplace-empty">
    <h2>No marketplace results found</h2>
    <p>Try a different search term or clear filters.</p>
    <button type="button" className="marketplace-button marketplace-button--secondary" onClick={onClear}>Clear filters</button>
  </div>
);

export default MarketplaceExperience;
